import store from '@/store'
import axios from '@/services/axiosInstance'

class AuthService {
  async login({ email, password }) {
    try {
      const response = await axios.post('/auth/login', {
        email,
        password
      })

      const { user, token } = response.data
      
      // Actualizar el store con el token y el usuario
      store.dispatch('loginSuccess', { user, token })

      try {
        const profileData = await this.getProfile()
        const normalizedProfile = {
          name: profileData.name || '',
          email: profileData.email || '',
          phone: profileData.phone || '',
          bio: profileData.bio || '',
          avatar: profileData.avatar || null,
          onboardingCompletedAt: profileData.onboardingCompletedAt || null,
          socialLinks: profileData.socialLinks || {
            linkedin: '',
            twitter: '',
            instagram: '',
            facebook: ''
          }
        }
        store.dispatch('updateUserProfile', normalizedProfile)
      } catch (profileError) {
        console.error('No se pudo cargar el perfil después del login:', profileError)
      }
      
      return response.data
    } catch (error) {
      // ✅ STEP 3: Preservar objeto completo del error para manejar SUBSCRIPTION_EXPIRED
      const errorData = error.response?.data || {};
      
      console.log('🔍 DEBUG AuthService - Error response completo:', {
        status: error.response?.status,
        data: errorData,
        token: errorData.token,
        code: errorData.code
      });
      
      // Crear error con propiedades adicionales
      const loginError = new Error(errorData.message || 'Error al iniciar sesión');
      loginError.code = errorData.code;
      loginError.redirectTo = errorData.redirectTo;
      loginError.expiryDate = errorData.expiryDate;
      loginError.token = errorData.token; // ✅ Capturar token temporal
      
      console.log('🔑 DEBUG AuthService - loginError properties:', {
        code: loginError.code,
        token: loginError.token,
        expiryDate: loginError.expiryDate
      });
      
      throw loginError;
    }
  }

  logout() {
    // Limpiar el store
    store.dispatch('logout')
  }

  isAuthenticated() {
    return store.getters.isAuthenticated
  }

  getCurrentUser() {
    return store.getters.currentUser
  }

  getToken() {
    return store.getters.token
  }

  async register(userData) {
    try {
      const response = await axios.post('/auth/register', userData)
      return response.data
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Error al registrar usuario')
    }
  }

  async registerWithInvitation(userData) {
    try {
      const response = await axios.post('/auth/register-invitation', userData)
      const { user, token } = response.data
      
      // Actualizar el store con el token y el usuario
      store.dispatch('loginSuccess', { user, token })
      
      return response.data
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Error al registrar usuario con código de invitación')
    }
  }

  async forgotPassword(email) {
    try {
      const response = await axios.post('/auth/forgot-password', { email })
      return response.data
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Error al procesar la solicitud')
    }
  }

  async resetPassword(token, newPassword) {
    try {
      const response = await axios.post('/auth/reset-password', {
        token,
        password: newPassword
      })
      return response.data
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Error al restablecer la contraseña')
    }
  }

  // Alias para requestPasswordReset
  async requestPasswordReset(email) {
    return this.forgotPassword(email)
  }

  async updatePassword(currentPassword, newPassword) {
    try {
      const response = await axios.post('/auth/update-password', {
        currentPassword,
        newPassword
      })
      return response.data
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Error al cambiar la contraseña')
    }
  }

  async getProfile() {
    try {
      const response = await axios.get('/auth/profile')
      return response.data.user
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Error al obtener el perfil')
    }
  }

  async updateProfile(profileData) {
    try {
      const response = await axios.put('/auth/profile', profileData)
      const { user } = response.data
      
      store.dispatch('updateUserProfile', user)
      
      return response.data
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Error al actualizar el perfil')
    }
  }
}

export default new AuthService() 